import PasskeyRegistrationDialog from '@/components/Mfa/PasskeyRegistrationDialog';
import RecoveryCodesDialog from '@/components/Mfa/RecoveryCodesDialog';
import TotpEnrollmentDialog from '@/components/Mfa/TotpEnrollmentDialog';
import { useLayoutStyles } from '@/hooks/styles';
import { passkeysSupported } from '@/utils/passkeys';
import { Button, Card, CardContent, CardHeader } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PhonelinkLockIcon from '@material-ui/icons/PhonelinkLock';
import VpnKeyOutlinedIcon from '@material-ui/icons/VpnKeyOutlined';
import React, { useState } from 'react';

const useStyles = makeStyles((theme) => ({
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

type Setup = 'passkey' | 'totp' | null;

interface SignInEnrollStepProps {
  email?: string;
  /**
   * Enrollment is done and the session is a full one. The page refetches
   * the viewer and moves on.
   */
  onDone: () => void;
  /** The enroll-only sign-in expired; the password must be entered again. */
  onRestart: (message?: string) => void;
}

/**
 * The sign-in step for a user whose account requires two-step
 * verification after grace (auth:mfa_enrollment_required). The session can only set up
 * a factor: a passkey or an authenticator app. The recovery codes follow,
 * and then the sign-in finishes.
 *
 * A browser without passkeys opens the authenticator app setup at once.
 */
export default function SignInEnrollStep({
  email,
  onDone,
  onRestart,
}: SignInEnrollStepProps): JSX.Element {
  const classes = useStyles();
  const layoutClasses = useLayoutStyles();
  const [passkeys] = useState(passkeysSupported);
  const [setup, setSetup] = useState<Setup>(passkeys ? null : 'totp');
  const [codes, setCodes] = useState<string[] | null>(null);

  function enrolled(recoveryCodes: string[]) {
    setSetup(null);
    setCodes(recoveryCodes);
  }

  function expired(message: string) {
    setSetup(null);
    onRestart(message);
  }

  const busy = !!setup || !!codes;

  return (
    <>
      <Card className={layoutClasses.card} variant="outlined">
        {/* Same top edge as the other sign-in cards. */}
        <div className={layoutClasses.progress} />
        <CardHeader
          title="Set up Two-Step Verification"
          subheader={`Your account requires a second step when you sign in${
            email ? ` as ${email}` : ''
          }. ${
            passkeys
              ? 'Set up a passkey or an authenticator app to continue.'
              : 'Set up an authenticator app to continue.'
          }`}
        />
        <CardContent>
          <div className={classes.actions}>
            {passkeys && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<VpnKeyOutlinedIcon />}
                disabled={busy}
                onClick={() => setSetup('passkey')}
              >
                Set up a passkey
              </Button>
            )}
            <Button
              variant={passkeys ? 'outlined' : 'contained'}
              color="primary"
              size="large"
              startIcon={<PhonelinkLockIcon />}
              disabled={busy}
              onClick={() => setSetup('totp')}
            >
              Set up authenticator app
            </Button>
          </div>
        </CardContent>
      </Card>

      <PasskeyRegistrationDialog
        open={setup === 'passkey'}
        onClose={() => setSetup(null)}
        onRegistered={enrolled}
        onSignInExpired={expired}
      />

      <TotpEnrollmentDialog
        open={setup === 'totp'}
        onClose={() => setSetup(null)}
        onEnrolled={enrolled}
        onSignInExpired={expired}
      />

      <RecoveryCodesDialog
        open={!!codes}
        codes={codes ?? []}
        email={email}
        onClose={onDone}
      />
    </>
  );
}
